import type {
  Vh3FrontendV2ApiDocumentationJobsJobFeedGetData,
  Vh3FrontendV2ApiDocumentationJobsModalIdGetData,
  Vh3FrontendV2ApiDocumentationJobsModalWorksheetsJobIdGetData,
} from './generated/Api';
import type { StringifyIds } from './stringify-ids';

// Job Status enum (business logic)
export enum JOB_STATUS {
  NEW = 'new',
  SCHEDULED = 'scheduled',
  UNSCHEDULED = 'unscheduled',
  SENT = 'sent',
  READ = 'read',
  ACCEPTED = 'accepted',
  REFUSED = 'refused',
  ON_THE_WAY = 'onTheWay',
  STARTED = 'started',
  SUSPENDED = 'suspended',
  COMPLETED_OK = 'completedOk',
  COMPLETED_WITH_ISSUES = 'completedWithIssues',
  CANCELLED = 'cancelled',
  LATE_START = 'lateStart',
  LATE_FINISH = 'lateFinish',
  RESCHEDULED = 'rescheduled',
}

// Worksheets types
export interface WorksheetItemQA {
  question: string;
  answer?: string;
}

export interface WorksheetsMap {
  [key: string]: WorksheetItemQA[];
}

export type Worksheets = WorksheetsMap;

// Job type - keeping for backwards compatibility with existing code
type GeneratedJobDetail =
  StringifyIds<Vh3FrontendV2ApiDocumentationJobsModalIdGetData>;

export type JobData = {
  id: string;
  typeId: string;
  typeName: string;
  contactId: string;
  contactName: string;
  contactAddress: string;
  contactLocation: {
    latitude: number;
    longitude: number;
  };
  status: JOB_STATUS;
  statusModifiedAt: string;
  plannedDuration: number;
  reference: string;
  description: string;
  createdAt: string;
  orderNumber: string;
  personId: string;
  personName: string;
  isConfirmed: boolean;
  categoryId: string;
  categoryName: string;
  resourceId: string;
  resourceName: string;
  vehicleId: string;
  vehicleRegistration: string;
  plannedStartAt: string;
  plannedEndAt: string;
  actualStartAt: string;
  actualEndAt: string;
  actualDuration: number;
  resourcesComment: string;
  contactComment: string;
  internalComment: string;
  result: string;
  jobGroupId: string;
  isFinanciallyComplete: boolean;
  isActioned: boolean;
  officeNotes: string;
  customFields: Array<{
    name: string;
    value: string;
  }>;
  vh3_ai: Array<{
    job_natural_summary?: string | null;
    job_description?: string | null;
    [key: string]: string | null | undefined;
  }>;
  worksheets: Worksheets;
} & GeneratedJobDetail;

// Extract stats type from generated response
type GeneratedStats = NonNullable<
  Vh3FrontendV2ApiDocumentationJobsJobFeedGetData['stats']
>;

// Extract pagination type from generated response
type GeneratedPagination = NonNullable<
  Vh3FrontendV2ApiDocumentationJobsJobFeedGetData['pagination']
>;

export type JobListStats = {
  'To Date': string;
  'From Date': string;
  'Total Jobs': number;
  'Total Actual Hours': number;
} & GeneratedStats;

export type JobPagination = {
  pageSize: number;
  pageNumber: number;
  pageItemCount: number;
} & GeneratedPagination;

export interface JobList {
  jobs: JobData[];
  stats?: JobListStats;
  pagination?: JobPagination;
  search_dates?: {
    startAtTo: string;
    startAtFrom: string;
  };
}

export type JobFilters = {
  types: string[];
  categories: string[];
  statuses: string[];
  resources: string[];
};

export type JobSortField =
  | 'reference'
  | 'statusModifiedAt'
  | 'plannedStartAt'
  | 'status'
  | 'typeName'
  | 'contactAddress';

export interface JobHistoryRequest {
  contactId: string;
  pageSize: number;
  pageNumber: number;
  startAtFrom: string;
  startAtTo: string;
  signal?: AbortSignal;
}

type GeneratedWorksheetResponse =
  Vh3FrontendV2ApiDocumentationJobsModalWorksheetsJobIdGetData;

export type WorksheetResponse = {
  worksheets: Worksheets;
} & GeneratedWorksheetResponse;

export type WebAgentSessionResponse = {
  agentId: string | null;
  sessionToken: string | null;
};

export type WebAgentSession = {
  agentId: string | null;
  sessionToken: string | null;
  job_history_summary: string;
};

export enum JobListType {
  ActiveJobs = 'active',
  PlannedJobs = 'planned',
}

export enum JobTabType {
  Active = 'active',
  Planned = 'planned',
  Overview = 'overview',
  Stats = 'stats',
}
export const JobListTypeToTab: Record<JobListType, JobTabType> = {
  [JobListType.ActiveJobs]: JobTabType.Active,
  [JobListType.PlannedJobs]: JobTabType.Planned,
};
